import { ReactElement, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { Alert, Stack, Typography } from '@mui/material'
import { ifcElementTypeLabels } from '@editor/components/Controls/IFCUploadWidget/constants/ifcFilter'
import { getGuidToMGroup } from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import {
  IFCParseAsRoofSlabForm,
  IFCParseAsWallForm,
} from '@editor/components/IFCElementForm/components'
import { CorneredSection } from '@ui2/structure'
import { useIfcGuidToHidden, useTranslucentGltfModel } from '@editorHooks'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import IFCImportToolsSection from '../IFCImportToolsSection'
import IFCSelectionInfo from '../IFCSelectionInfo'

const typeListFormat = new Intl.ListFormat('de', { type: 'conjunction' })

const WALL_TYPES = ['outer_walls', 'inner_walls']

interface Props {
  /**
   * Assigned element types to import, e.g. `['roof_slabs']` or `['outer_walls', 'inner_walls']`.
   * Walls of both placements are imported in one step, each with the placement of its own type.
   */
  elementTypes: string[]
}

/**
 * Import step of the guided IFC import for one or more assigned element types: pre-selects all
 * elements of those types so the user can review/adjust which ones get imported, then triggers the
 * actual IFC parse for the current selection. Selection + clear ("Auswahl aufheben") reuse the same
 * IFC element selection as the assignment steps; the import itself reuses the parse forms from
 * `IFCElementForm` (the free "Element importieren" flow).
 */
const IFCElementImportPanel = ({ elementTypes }: Props): ReactElement => {
  const { projectId = '' } = useParams()
  const { metadata, activeGlbFile, selectedIfcElementGuids, setSelectedIfcElementGuids } =
    useIFCElementSelection()

  // Frontend shows the GLB (converted from IFC); the parse task needs the raw IFC file id = parent.
  const ifcFileId = activeGlbFile?.parent_guid ?? null

  // Render the GLB translucent while importing so elements stay visible.
  useTranslucentGltfModel()

  // Same hidden/filtered map the scene uses, so we only pre-select what is currently visible.
  const guidToHidden = useIfcGuidToHidden(metadata)

  // Type of every IFC element, whether it carries the type as its default IFC MGroup or via an
  // explicit assignment (`getGuidToMGroup` merges both, assignments overriding defaults).
  const guidToType = useMemo(() => getGuidToMGroup(metadata), [metadata])

  // Pre-select every visible element of the step's types. Elements hidden or filtered out in the
  // scene are excluded.
  const elementGuids = useMemo(
    () =>
      Object.entries(guidToType)
        .filter(([guid, type]) => elementTypes.includes(type) && !guidToHidden[guid])
        .map(([guid]) => guid),
    [guidToType, elementTypes, guidToHidden],
  )

  // Walls are imported with the placement of their own type, so inner and outer walls can be
  // selected — and imported — together.
  const placementByGuid = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(guidToType)
          .filter(([, type]) => WALL_TYPES.includes(type))
          .map(([guid, type]) => [guid, type === 'outer_walls' ? 'External' : 'Internal']),
      ) as Record<string, WallPlacement>,
    [guidToType],
  )

  // Key the auto-select on the guid *content*, not the array reference, so a reference-only metadata
  // refetch (React Query without structural sharing) doesn't re-run this and wipe a manual
  // deselection. It still re-runs when the type's element set genuinely changes (e.g. data arriving
  // after mount). Read the latest guids from a ref so they aren't a dependency.
  const elementGuidsKey = elementGuids.join(',')
  const elementGuidsRef = useLatest(elementGuids)
  useEffect(() => {
    setSelectedIfcElementGuids(elementGuidsRef.current)
  }, [elementGuidsKey, elementGuidsRef, setSelectedIfcElementGuids])

  const typeList = typeListFormat.format(
    elementTypes.map(type => `„${ifcElementTypeLabels[type] ?? type}“`),
  )
  const hasWallTypes = elementTypes.some(type => WALL_TYPES.includes(type))

  // Elements selected by hand that carry no wall type have no placement of their own — the parse
  // form falls back to "Außenwand" for them, which we spell out rather than deciding silently.
  const guidsWithoutPlacement = hasWallTypes
    ? selectedIfcElementGuids.filter(guid => !placementByGuid[guid]).length
    : 0

  return (
    <Stack spacing={2}>
      <CorneredSection title="Auswahl">
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Alle Elemente vom Typ {typeList} sind vorausgewählt. Passen Sie die Auswahl bei Bedarf
            an.
          </Typography>

          <IFCSelectionInfo showWhenEmpty />

          {elementTypes.includes('roof_slabs') && (
            <IFCParseAsRoofSlabForm
              guids={selectedIfcElementGuids}
              ifcFileId={ifcFileId}
              projectId={projectId}
              onSuccess={() => setSelectedIfcElementGuids([])}
            />
          )}

          {guidsWithoutPlacement > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              {guidsWithoutPlacement === 1
                ? '1 ausgewähltes Element hat keine Wandtyp-Zuweisung und wird als Außenwand importiert.'
                : `${guidsWithoutPlacement} ausgewählte Elemente haben keine Wandtyp-Zuweisung und werden als Außenwand importiert.`}
            </Alert>
          )}

          {hasWallTypes && (
            <IFCParseAsWallForm
              guids={selectedIfcElementGuids}
              ifcFileId={ifcFileId}
              projectId={projectId}
              placement="External"
              placementByGuid={placementByGuid}
              onSuccess={() => setSelectedIfcElementGuids([])}
            />
          )}
        </Stack>
      </CorneredSection>

      <IFCImportToolsSection showImportHelper={hasWallTypes} />
    </Stack>
  )
}

export default IFCElementImportPanel
